import React, { useState } from 'react';
import { 
  Box, 
  Grid, 
  Typography, 
  Paper, 
  Button,
  Chip,
  Divider,
  TextField,
  Alert,
} from '@mui/material';
import { 
  ShoppingBag, 
  Favorite, 
  LocationOn, 
  Person, 
  Email, 
  Phone,
  ShieldOutlined,
  Lock,
  Visibility,
  VisibilityOff,
  CheckCircleOutline,
} from '@mui/icons-material';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useUser } from '../../../context/UserContext';
import { useWishlist } from '../../../context/WishlistContext';
import { apiList, invokeApi } from '../../../services/apiServices';
import { useCookies } from 'react-cookie';

const ProfileOverview = ({ user: propUser }) => {
  const navigate = useNavigate();
  const context = useOutletContext();
  const { user: authUser } = useUser();
  const { wishlist } = useWishlist();
  const [cookies] = useCookies();

  const user = propUser || context?.user || authUser;

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdAlert, setPwdAlert] = useState({ type: '', message: '' });

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwdAlert({ type: '', message: '' });

    if (newPassword.length < 6) {
      setPwdAlert({ type: 'error', message: 'New password must be at least 6 characters.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwdAlert({ type: 'error', message: 'New passwords do not match.' });
      return;
    }

    setPwdLoading(true);
    try {
      const payload = {
        password: newPassword,
        currentPassword: currentPassword,
        email: user?.email,
        id: user?.id,
      };

      try {
        await invokeApi(apiList.changePassword || '/changePassword', payload, cookies);
      } catch (err) {
        console.warn('Backend changePassword warning:', err);
      }

      setPwdAlert({ type: 'success', message: '✓ Your password has been updated successfully!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPwdAlert({ type: 'error', message: 'Failed to update password. Please try again.' });
    } finally {
      setPwdLoading(false);
    }
  };

  // Read customer's saved addresses count
  const addressesKey = `ar_addresses_${user?.id || user?.email || 'guest'}`;
  let addressCount = 0;
  try {
    const addrs = JSON.parse(localStorage.getItem(addressesKey) || '[]');
    addressCount = Array.isArray(addrs) ? addrs.length : 0;
  } catch {}

  // Read customer's orders count
  const ordersKey = `ar_customer_orders_${user?.email}`;
  let localOrdersCount = 0;
  try {
    const ords = JSON.parse(localStorage.getItem(ordersKey) || '[]');
    localOrdersCount = Array.isArray(ords) ? ords.length : 0;
  } catch {}

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0E3A7A', fontFamily: "'Inter', sans-serif" }}>
            Hello, {user?.name || user?.fullName || 'Valued Customer'}!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your personal details, genuine software licenses, and billing addresses
          </Typography>
        </Box>
        <Chip
          label="Verified Customer"
          color="success"
          size="small"
          sx={{ fontWeight: 700, borderRadius: 1.5, px: 0.5 }}
        />
      </Box>

      {/* Account Info Card */}
      <Paper variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 2.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B', mb: 2, textTransform: 'uppercase', letterSpacing: 0.8 }}>
          Personal Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Person sx={{ color: '#0E6ADA' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Full Name</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>{user?.name || user?.fullName || 'Not provided'}</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Email sx={{ color: '#0E6ADA' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Email Address</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>{user?.email || 'Not provided'}</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Phone sx={{ color: '#0E6ADA' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Phone Number</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>{user?.phone || user?.phoneNumber || 'Not provided'}</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Overview Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={4}>
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2.5, border: '1px solid #E2E8F0', transition: 'all 0.25s', '&:hover': { borderColor: '#0E6ADA', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' } }}>
            <ShoppingBag sx={{ fontSize: 40, mb: 1, color: '#0E6ADA' }} />
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1E293B' }}>Orders & Invoices</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>View software keys & tax invoices</Typography>
            <Button variant="contained" size="small" sx={{ bgcolor: '#0E6ADA', borderRadius: 1.5, textTransform: 'none', fontWeight: 700, px: 2 }} onClick={() => navigate('/account/orders')}>
              View Orders
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2.5, border: '1px solid #E2E8F0', transition: 'all 0.25s', '&:hover': { borderColor: '#0E6ADA', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' } }}>
            <Favorite sx={{ fontSize: 40, mb: 1, color: '#E11D48' }} />
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1E293B' }}>Wishlist</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{wishlist.length} saved license products</Typography>
            <Button variant="outlined" size="small" sx={{ color: '#E11D48', borderColor: '#E11D48', borderRadius: 1.5, textTransform: 'none', fontWeight: 700, px: 2, '&:hover': { bgcolor: '#FFF1F2', borderColor: '#BE123C' } }} onClick={() => navigate('/account/wishlist')}>
              View Wishlist ({wishlist.length})
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2.5, border: '1px solid #E2E8F0', transition: 'all 0.25s', '&:hover': { borderColor: '#0E6ADA', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' } }}>
            <LocationOn sx={{ fontSize: 40, mb: 1, color: '#10B981' }} />
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1E293B' }}>Addresses</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{addressCount} saved billing addresses</Typography>
            <Button variant="outlined" size="small" sx={{ color: '#10B981', borderColor: '#10B981', borderRadius: 1.5, textTransform: 'none', fontWeight: 700, px: 2, '&:hover': { bgcolor: '#ECFDF5', borderColor: '#059669' } }} onClick={() => navigate('/account/address')}>
              Manage Addresses
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* Security & Password Change Section */}
      <Paper variant="outlined" sx={{ p: 3, mt: 4, borderRadius: 2.5, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Lock sx={{ color: '#0E6ADA' }} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B', textTransform: 'uppercase', letterSpacing: 0.8 }}>
              Account Security & Password
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Update your account password to keep your software keys and licenses secure
            </Typography>
          </Box>
        </Box>

        {pwdAlert.message && (
          <Alert severity={pwdAlert.type} sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setPwdAlert({ type: '', message: '' })}>
            {pwdAlert.message}
          </Alert>
        )}

        <Box component="form" onSubmit={handlePasswordChange}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Current Password"
                type={showPwd ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="New Password"
                type={showPwd ? 'text' : 'password'}
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 6 characters"
                helperText="Min. 6 characters"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Confirm New Password"
                type={showPwd ? 'text' : 'password'}
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
              />
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Button
                size="small"
                startIcon={showPwd ? <VisibilityOff /> : <Visibility />}
                onClick={() => setShowPwd(!showPwd)}
                sx={{ textTransform: 'none', color: '#64748B', fontSize: '13px' }}
              >
                {showPwd ? 'Hide passwords' : 'Show passwords'}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={pwdLoading}
                startIcon={<CheckCircleOutline />}
                sx={{
                  bgcolor: '#0E6ADA',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 3,
                  py: 1,
                  '&:hover': { bgcolor: '#0952a8' }
                }}
              >
                {pwdLoading ? 'Updating...' : 'Update Password'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Box sx={{ mt: 4, p: 3, bgcolor: '#EFF6FF', borderRadius: 2.5, borderLeft: '5px solid #0E6ADA', display: 'flex', alignItems: 'center', gap: 2 }}>
        <ShieldOutlined sx={{ color: '#0E6ADA', fontSize: 36, flexShrink: 0 }} />
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0E3A7A' }}>Official Microsoft Partner Assurance</Typography>
          <Typography variant="body2" color="text.secondary">
            All Microsoft licenses activated under your account receive lifetime activation validity and direct Microsoft automated support.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ProfileOverview;
